import ReactDOM from 'react-dom/client';
import type { Root } from 'react-dom/client';
import '../src/assets/styles/global.scss';
import '../src/assets/styles/airports.scss';
import App from './App';
import 'antd/dist/reset.css';
import './i18n';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './queryClient';
import { SpinProvider } from './components/SpinContext';
import Spinner from './components/Spinner';
import { Provider } from 'react-redux';
import { persistor, store } from './redux-toolkit/store';
import { WebSocketProvider } from '@/context/WebSocketContext';
import { init as initFullStory } from '@fullstory/browser';
import { BrowserRouter } from 'react-router-dom';
import OktaProvider from './OktaProvider';
import { RefreshTokenProvider } from '@/context/RefreshTokenContext';
import PersistGateCompat from './components/PersistGateCompat';
import StatsigAppProvider from './StatsigAppProvider';
import {
  initActivityBeacon,
  sendPendingActivityNow,
} from '@/hooks/useActivityBeacon/useActivityBeacon';
import { onBeforeStorageClear } from '@/common/helper/storageCleanup';

initFullStory({
  orgId: 'o-22MP9G-na1',
  host: 'metrics.zoomlogi.com',
  script: 'metrics.zoomlogi.com/s/fs.js',
  appHost: 'app.fullstory.com',
});

initActivityBeacon(store, persistor);
onBeforeStorageClear(sendPendingActivityNow);

const RootComponent = () => {
  return (
    <Provider store={store}>
      <PersistGateCompat loading={null} persistor={persistor}>
        <RefreshTokenProvider>
          <BrowserRouter>
            <OktaProvider>
              <WebSocketProvider>
                <SpinProvider>
                  <Spinner />
                  <QueryClientProvider client={queryClient}>
                    <StatsigAppProvider>
                      <App />
                    </StatsigAppProvider>
                  </QueryClientProvider>
                </SpinProvider>
              </WebSocketProvider>
            </OktaProvider>
          </BrowserRouter>
        </RefreshTokenProvider>
      </PersistGateCompat>
    </Provider>
  );
};

if (process.env.NODE_ENV === 'production') {
  console.warn = () => {};
  console.error = () => {};
  console.info = () => {};
  console.debug = () => {};
}

declare global {
  interface Window {
    __zoomlogiReactRoot?: Root;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

const root = window.__zoomlogiReactRoot ?? ReactDOM.createRoot(rootElement);
window.__zoomlogiReactRoot = root;

root.render(<RootComponent />);
